import React, { useContext } from 'react';
import styled from 'styled-components';
import { usePostHog } from 'posthog-js/react';
import { ThemeContext } from '../ThemeContext';

const Container = styled.div`
  padding: 10px;
`;

const Title = styled.h2`
  margin-bottom: 20px;
  color: ${props => props.theme === 'light' ? '#333' : '#e0e0e0'};
  
  .highlight {
    color: var(--highlight-color-${props => props.theme === 'light' ? 'light' : 'dark'});
  }
`;

const Paragraph = styled.p`
  margin-bottom: 15px;
  line-height: 1.6;
  color: ${props => props.theme === 'light' ? '#555' : '#b0b0b0'};

  strong {
    color: var(--highlight-color-${props => props.theme === 'light' ? 'light' : 'dark'});
  }
`;

const Form = styled.form`
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 500px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 6px;
  font-weight: 500;
  color: ${props => props.theme === 'light' ? '#333' : '#e0e0e0'};
`;

const Input = styled.input`
  padding: 10px;
  border-radius: 5px;
  border: 1px solid ${props => props.theme === 'light' ? '#ccc' : '#444'};
  background: ${props => props.theme === 'light' ? 'white' : '#232323'};
  color: ${props => props.theme === 'light' ? '#333' : '#e0e0e0'};
  font-size: 1rem;
  transition: border 0.2s;
  &:focus {
    border-color: var(--highlight-color-${props => props.theme === 'light' ? 'light' : 'dark'});
    outline: none;
  }
`;

const TextArea = styled.textarea`
  padding: 10px;
  border-radius: 5px;
  border: 1px solid ${props => props.theme === 'light' ? '#ccc' : '#444'};
  background: ${props => props.theme === 'light' ? 'white' : '#232323'};
  color: ${props => props.theme === 'light' ? '#333' : '#e0e0e0'};
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  transition: border 0.2s;
  &:focus {
    border-color: var(--highlight-color-${props => props.theme === 'light' ? 'light' : 'dark'});
    outline: none;
  }
`;

const SubmitButton = styled.button`
  background: var(--highlight-color-${props => props.theme === 'light' ? 'light' : 'dark'});
  color: white;
  border: none;
  border-radius: 5px;
  padding: 12px 0;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
  &:hover {
    background: var(--highlight-hover-${props => props.theme === 'light' ? 'light' : 'dark'});
    transform: translateY(-2px);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  margin-top: 18px;
  color: ${props => props.success ? 'green' : 'red'};
  font-weight: 500;
`;

const ContactSection = () => {
  const { theme } = useContext(ThemeContext);
  const posthog = usePostHog();

  const [formData, setFormData] = React.useState({ name: '', email: '', message: '' });
  const [status, setStatus] = React.useState({ submitting: false, success: '', error: '' });
  const [hasStarted, setHasStarted] = React.useState(false);

  const handleChange = e => {
    if (!hasStarted) {
      posthog?.capture('contact_form_started', { first_field: e.target.name });
      setHasStarted(true);
    }
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    posthog?.capture('contact_form_submitted');
    setStatus({ submitting: true, success: '', error: '' });
    try {
      const res = await fetch('https://formspree.io/f/xzzvaogk', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: new FormData(e.target)
      });
      const data = await res.json();
      if (res.ok) {
        posthog?.capture('contact_form_success');
        setStatus({ submitting: false, success: 'Thank you! Your message has been sent.', error: '' });
        setFormData({ name: '', email: '', message: '' });
      } else {
        // Formspree returns errors in data.errors (array of {field, message, code})
        let errorMsg = 'Something went wrong. Please try again.';
        if (data && Array.isArray(data.errors) && data.errors.length > 0) {
          // Prioritize field-specific errors
          for (const err of data.errors) {
            if (err.field === 'email' && err.code === 'type') {
              errorMsg = 'Please enter a valid email.';
              break;
            }
          }
          // If no match, show first error message
          if (errorMsg === 'Something went wrong. Please try again.') {
            errorMsg = data.errors[0].message || errorMsg;
          }
          // If the error message is the default Formspree email error, override it
          if (typeof errorMsg === 'string' && errorMsg.toLowerCase().includes('should be an email')) {
            errorMsg = 'Please enter a valid email.';
          }
        } else if (data && data.error) {
          errorMsg = data.error;
        }
        posthog?.capture('contact_form_error', { error: errorMsg });
        setStatus({ submitting: false, success: '', error: errorMsg });
      }
    } catch (err) {
      posthog?.capture('contact_form_error', { error: 'network_error' });
      setStatus({ submitting: false, success: '', error: 'Something went wrong. Please try again.' });
    }
  };

  return (
    <Container>
      <Title theme={theme}><span className="highlight">Contact</span> Me</Title>
      <Paragraph theme={theme}>
        I'd love to hear from you! Fill out the form below and I'll get back to you as soon as I can.
      </Paragraph>
      <Form onSubmit={handleSubmit} autoComplete="off">
        <InputGroup>
          <Label theme={theme} htmlFor="name">Name</Label>
          <Input
            theme={theme}
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            autoComplete="off"
          />
        </InputGroup>
        <InputGroup>
          <Label theme={theme} htmlFor="email">Email</Label>
          <Input
            theme={theme}
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            autoComplete="off"
          />
        </InputGroup>
        <InputGroup>
          <Label theme={theme} htmlFor="message">Message</Label>
          <TextArea
            theme={theme}
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
          />
        </InputGroup>
        {/* Honeypot field for spam protection */}
        <input type="text" name="_gotcha" style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />
        <SubmitButton theme={theme} type="submit" disabled={status.submitting}>
          {status.submitting ? 'Sending...' : 'Send Message'}
        </SubmitButton>
      </Form>
      {status.success && (
        <Message theme={theme} success>{status.success}</Message>
      )}
      {status.error && (
        <Message theme={theme}>{status.error}</Message>
      )}
    </Container>
  );
};

export default ContactSection; 